import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { register, sendVerifyCodeForRegister } from '../../api';
import { App, Tooltip } from 'antd';
import InputField from '../Form/InputField';
import Button from '../Form/Button';
import { ERROR_MESSAGES } from '../../utils/constants';
import { validatePassword } from '../../utils/tools.js';
import icon from '@/utils/icon.js';

const RegisterBox = ({ onSuccess, onSwitchToLogin, showCloseBtn = false, onClose }) => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    phone: '',
    verifyCode: '',
    psw: '',
    confirmPsw: '',
    company: '',
    incode: '',
    email: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const navigate = useNavigate();
  const { message } = App.useApp();

  useEffect(() => {
    // 从 URL 的 invite 参数读取邀请码（与邀请链接的 ?invite=xxx 对应）
    // 接口传参仍保持 incode 不变，这里只是做参数名映射
    const params = new URLSearchParams(location.search);
    const invite = params.get('invite');
    if (invite) {
      setFormData(prev => ({ ...prev, incode: invite }));
    }
  }, [location]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.phone) {
      setError('注册电话号码不能为空');
      setLoading(false);
      return;
    }

    if (!formData.verifyCode) {
      setError('验证码不能为空');
      setLoading(false);
      return;
    }

    if (!formData.psw) {
      setError('注册密码不能为空');
      setLoading(false);
      return;
    }

    const pwdCheck = validatePassword(formData.psw);
    if (!pwdCheck.ok) {
      setError(pwdCheck.msg);
      setLoading(false);
      return;
    }

    if (formData.psw !== formData.confirmPsw) {
      setError('两次输入的密码不一致');
      setLoading(false);
      return;
    }

    try {
      const result = await register({
        phone: formData.phone,
        password: formData.psw,
        verifyCode: formData.verifyCode,
        company: '', // 注册表单暂无公司名输入框，默认传空
        inviteCode: formData.incode,
        email: formData.email,
      });
      if (result.errorCode === 0) {
        message.success('注册成功，请登录');
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/login');
        }
      } else {
        setError(result.errorMsg || '注册失败');
      }
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!formData.phone) {
      setError('请输入手机号');
      return;
    }

    setSendingCode(true);
    try {
      const result = await sendVerifyCodeForRegister(formData.phone);
      if (result.errorCode === 0 || result.errorCode === "0") {
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        message.success('验证码已发送');
      } else {
        setError(result.errorMsg || result.message || '验证码发送失败');
      }
    } catch (err) {
      console.error('发送验证码失败:', err);
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setSendingCode(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
    setError('');
  };

  

  return (
    <div className="relative w-full max-w-sm bg-[#1A1C1B] border border-white/20 rounded-xl p-8 text-white pt-[24px]">
      <img src={icon.moldAISvg2} className='h-9'></img>
      {showCloseBtn && (
        <button 
          onClick={onClose}
          className="absolute top-7 right-6 text-white/40 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <h1 className="text-2xl font-medium mb-8">注册账号</h1>

      <form className="relative space-y-6" onSubmit={handleSubmit}>
        <div className="relative">
          <InputField
            type="tel"
            placeholder="手机号"
            id="phone"
            name="phone"
            autoComplete="tel"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="relative">
          <InputField
            type="text"
            placeholder="验证码"
            id="verifyCode"
            name="verifyCode"
            autoComplete="one-time-code"
            value={formData.verifyCode}
            onChange={handleChange}
            className="pr-24"
          />
          <button
            type="button"
            className="absolute right-0 top-0 h-full px-4 text-sm text-[#61EA3C] disabled:text-gray-500"
            onClick={handleSendCode}
            disabled={countdown > 0 || sendingCode}
          >
            {countdown > 0 ? `${countdown}s` : sendingCode ? '发送中...' : '发送验证码'}
          </button>
        </div>

        <Tooltip
          title="密码长度 8-14 位，可包含字母、数字和英文标点，不支持空格、中文；字母/数字以及标点符号至少包含2种"
          placement="topLeft"
          trigger={['focus']}
          getPopupContainer={(trigger) => trigger.parentElement}
          overlayStyle={{ width: '100%', maxWidth: 'none' }}
        >
          <InputField
            type="password"
            placeholder="密码"
            id="psw"
            name="newPassword"
            autoComplete="new-password"
            value={formData.psw}
            onChange={handleChange}
          />
        </Tooltip>

        <InputField
          type="password"
          placeholder="确认密码"
          id="confirmPsw"
          name="confirmPassword"
          autoComplete="new-password"
          value={formData.confirmPsw}
          onChange={handleChange}
        />

        <InputField
          type="text"
          placeholder="邀请码（可选）"
          id="incode"
          name="incode"
          autoComplete="off"
          value={formData.incode}
          onChange={handleChange}
        />

        <InputField
          type="email"
          placeholder="邮箱（可选）"
          id="email"
          name="email"
          autoComplete="email"
          value={formData.email}
          onChange={handleChange}
        />

        {error && (
          <span className="regist_errMsg text-left text-sm text-rose-500 block">
            {error}
          </span>
        )}

        <Button type="submit" disabled={loading}>
          {loading ? '注册中...' : '注册'}
        </Button>
      </form>

      <div className="text-center text-sm text-gray-400 mt-6">
        已有账号?
        {onSwitchToLogin ? (
          <button 
            type="button"
            onClick={onSwitchToLogin} 
            className="text-[#61EA3C] hover:underline ml-1"
          >
            去登录
          </button>
        ) : (
          <Link to="/login" className="text-[#61EA3C] hover:underline ml-1">
            去登录
          </Link>
        )}
      </div>
    </div>
  );
};

export default RegisterBox;
